import {
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  CpuChipIcon,
  FolderArrowDownIcon,
  MinusCircleIcon,
  PencilIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import type { ColDef } from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import { NotesIconRenderer } from '~/components/agGrid/renderers';
import type { AIParseJob, DocumentWithAiJobs } from '~/features/vault/documents/types';
import { filterRenderableAiJobs } from '~/features/vault/documents/utils';

export const BASE_GRID_STYLE = {
  backgroundColor: 'var(--grey-950)',
  foregroundColor: 'var(--grey-100)',
  headerBackgroundColor: 'var(--grey-950)',
  headerTextColor: 'var(--grey-200)',
  headerFontWeight: '600',
  headerFontSize: '13px',
  headerColumnBorder: false,
  pinnedColumnBorder: false,
  headerRowBorder: '1px solid var(--grey-800)',
  rowBorder: '1px solid var(--grey-900)',
  wrapperBorder: 'none',
  borderColor: 'var(--grey-800)',
  fontFamily: 'Inter',
  fontSize: '14px',
  oddRowBackgroundColor: 'var(--grey-950)',
  rowHoverColor: 'var(--primary-t-950)',
  selectedRowBackgroundColor: 'var(--primary-t-900)',
  columnHoverColor: 'transparent',
  borderRadius: '0px',
  cellHorizontalPadding: '16px',
  headerCellHoverBackgroundColor: 'transparent',
  footerRowBorder: '1px solid var(--grey-800)',
  paginationBackgroundColor: 'var(--grey-950)',
};

const actionBtnClass =
  'flex justify-center items-center w-full h-full cursor-pointer rounded-md p-1.5 transition-colors';

export const DeleteCellRenderer = (
  props: CustomCellRendererProps & { onDelete: (id: number) => void; cyTag: string }
) => {
  const handleDeleteClick = () => {
    props.onDelete(props.data.id);
  };
  return (
    <button
      className={`${actionBtnClass} text-grey-400 hover:text-error-500 hover:bg-error-50/10`}
      type="button"
      title="Delete"
      onClick={handleDeleteClick}
      data-cy={`${props.cyTag}-${props.node.rowIndex}`}
      aria-label="Delete"
    >
      <MinusCircleIcon aria-hidden="true" className="size-4 shrink-0" />
    </button>
  );
};

export const EditCellRenderer = (
  props: CustomCellRendererProps & { onEdit: (id: number) => void; cyTag: string }
) => {
  const handleEditClick = () => {
    props.onEdit(props.data.id);
  };
  return (
    <button
      className={`${actionBtnClass} text-grey-400 hover:text-primary-50 hover:bg-primary-t-900`}
      type="button"
      title="Edit Document"
      onClick={handleEditClick}
      data-cy={`${props.cyTag}-${props.node.rowIndex}`}
      aria-label="Edit"
    >
      <PencilIcon aria-hidden="true" className="size-4 shrink-0" />
    </button>
  );
};

export const DownloadCellRenderer = (
  props: CustomCellRendererProps & { onDownload: (id: number) => void; cyTag: string }
) => {
  const handleDownloadClick = () => {
    props.onDownload(props.data.id);
  };
  return (
    <button
      className={`${actionBtnClass} text-grey-400 hover:text-primary-50 hover:bg-primary-t-900`}
      type="button"
      title="Download"
      onClick={handleDownloadClick}
      data-cy={`${props.cyTag}-${props.node.rowIndex}`}
      aria-label="Download"
    >
      <FolderArrowDownIcon aria-hidden="true" className="size-4 shrink-0" />
    </button>
  );
};

export const ParseDocumentCellRenderer = (
  props: CustomCellRendererProps & { onParse: (id: number) => void; cyTag: string }
) => {
  const handleParse = () => {
    props.onParse(props.data.id);
  };
  const disabled = props.data.aiJobs.length >= 4;
  const color = disabled ? 'text-grey-600' : 'text-primary-t-300';
  return (
    <button
      className={`${actionBtnClass} ${color} hover:bg-primary-t-900`}
      disabled={disabled}
      type="button"
      onClick={handleParse}
      data-cy={`${props.cyTag}-${props.node.rowIndex}`}
      // [MCV-1187] Match EditCellRenderer's `title` pattern so the browser's
      // native tooltip reads clearly on hover instead of relying on aria-label
      // (which most browsers don't surface visually).
      title={disabled ? 'AI parse limit reached for this document' : 'Trigger AI document parsing'}
      aria-label="Ai-Parse"
    >
      <CpuChipIcon aria-hidden="true" className={`size-5 shrink-0 hover:text-primary-50`} />
    </button>
  );
};

export const AiJobStatusCellRenderer = (
  props: CustomCellRendererProps & { data: DocumentWithAiJobs; cyTag: string }
) => {
  const statusConfig = {
    pending: { label: 'Pending', color: 'text-grey-400', icon: ClockIcon },
    started: { label: 'Processing', color: 'text-primary-50', icon: ArrowPathIcon },
    processing: { label: 'Processing', color: 'text-primary-50', icon: ArrowPathIcon },
    completed: { label: 'Completed', color: 'text-good', icon: CheckCircleIcon },
    error: { label: 'Failed', color: 'text-error-50', icon: XCircleIcon },
    failed: { label: 'Failed', color: 'text-error-50', icon: XCircleIcon },
  } as const;

  const sortedJobs = [...filterRenderableAiJobs(props.data.aiJobs)].sort(
    (a: AIParseJob, b: AIParseJob) => {
      return new Date(a?.updated_at).getTime() - new Date(b?.updated_at).getTime();
    }
  );

  if (sortedJobs.length === 0) return null;

  const latestJob = sortedJobs[sortedJobs.length - 1];
  const latestStatusKey = latestJob?.job_status?.toLowerCase() as keyof typeof statusConfig;
  const latestStatusConfig = statusConfig[latestStatusKey];

  if (!latestStatusConfig) return null;

  return (
    <span className="flex items-center gap-1">
      {sortedJobs.map((job: AIParseJob) => {
        const statusKey = job?.job_status?.toLowerCase() as keyof typeof statusConfig;
        const statusConfigValue = statusConfig[statusKey];
        if (!statusConfigValue) return null;

        const StatusIcon = statusConfigValue.icon;

        const isError = statusKey === 'error' || statusKey === 'failed';
        return (
          <div
            key={String(job.id)}
            className="flex items-center gap-1"
            title={isError && job.error ? job.error : undefined}
          >
            <StatusIcon
              aria-hidden="true"
              className={`size-4 shrink-0 ${statusConfigValue.color} ${statusKey === 'started' || statusKey === 'processing' ? 'animate-spin' : ''}`}
            />
          </div>
        );
      })}

      <span className={latestStatusConfig.color}>{latestStatusConfig.label}</span>
    </span>
  );
};

export const AiJobConfidenceCellRenderer = (
  props: CustomCellRendererProps & { data: DocumentWithAiJobs; cyTag: string }
) => {
  const sortedJobs = [...props.data.aiJobs].sort((a: AIParseJob, b: AIParseJob) => {
    return new Date(b?.updated_at).getTime() - new Date(a?.updated_at).getTime();
  });

  return (
    <>
      <span> {sortedJobs[0]?.process_confidence}</span>
    </>
  );
};

export const AiJobCountCellRenderer = (
  props: CustomCellRendererProps & { data: DocumentWithAiJobs; cyTag: string }
) => {
  return (
    <>
      <span> {props.data.aiJobs.length}</span>
    </>
  );
};

export const ACTION_COLUMN_STYLES = {
  field: '',
  width: 40,
  flex: 0,
  resizable: false,
  cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
  editable: false,
  sortable: false,
  filter: false,
  // [MCV-1567] No `headerClass: 'cursor-pointer'` here -- see ACTIONS_COLUMN_BASE.
};

/** Base styles for a single merged "Actions" column containing multiple buttons */
export const ACTIONS_COLUMN_BASE: ColDef = {
  headerName: 'Actions',
  // [MCV-1405] Explicit colId so ag-grid never falls back to an index-derived
  // one (e.g. "0") — a positional id silently rebinds when a grid's column set
  // changes, letting a stale persisted layout land Actions in the wrong slot.
  colId: 'actions',
  field: '',
  flex: 0,
  minWidth: 90,
  resizable: false,
  pinned: 'right' as const,
  sortable: false,
  filter: false,
  editable: false,
  // [MCV-1405] sortable/filter/resizable false above don't stop dragging —
  // lock the column in place so Actions can never be reordered or dragged out
  // of its pinned container (which would silently unpin it).
  suppressMovable: true,
  lockPosition: 'right' as const,
  lockPinned: true,
  // [MCV-1567] Deliberately NO `cursor-pointer` in `headerClass` (which now only
  // carries the right-alignment class below), reversing MCV-1479.
  //
  // MCV-1479 read the arrow cursor as an inconsistency to paper over: every
  // other header showed a hand, so Actions was made to match. But the hand on
  // those headers is not decoration -- ag-grid puts `cursor: pointer` on
  // `.ag-header-cell-sortable` precisely because clicking them sorts. This
  // column is `sortable: false`, `filter: false`, `resizable: false`,
  // `suppressMovable: true`, `lockPosition: 'right'`, `lockPinned: true`: there
  // is no gesture it accepts. Borrowing the affordance advertised an
  // interaction that six separate flags above exist to forbid, and clicking it
  // did nothing.
  //
  // The difference in cursor IS the correct signal. Do not "restore" this to
  // match the other headers without re-reading MCV-1479 and MCV-1567 together.
  // [MCV-1849] Right-aligned so rows with fewer icons (e.g. a `show:` predicate
  // hides one) end on the same right edge as rows with more, instead of being
  // centred and drifting out of line. The header label is right-aligned with
  // ag-grid's built-in class to sit over the icons; matches the shared Table fix.
  headerClass: 'ag-right-aligned-header',
  cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '2px' },
};

export type ActionDef = {
  type: 'edit' | 'delete' | 'download' | 'parse' | 'notes';
  handler: (id: number) => void;
  cyTag: string;
  /** Optional predicate — return false to hide the button for a given row */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  show?: (data: any) => boolean;
  /** Override which data field to pass to the handler (defaults to 'id') */
  idField?: string;
};

const ACTION_RENDERERS = {
  edit: EditCellRenderer,
  delete: DeleteCellRenderer,
  download: DownloadCellRenderer,
  parse: ParseDocumentCellRenderer,
  notes: NotesIconRenderer,
} as const;

const handlerPropMap = {
  edit: 'onEdit',
  delete: 'onDelete',
  download: 'onDownload',
  parse: 'onParse',
  notes: null,
} as const;

/** Generic cell renderer that combines multiple action buttons in a single column */
export const ActionsRenderer = (props: CustomCellRendererProps & { actions: ActionDef[] }) => {
  return (
    <div className="flex items-center justify-end gap-1">
      {props.actions.map((action) => {
        if (action.show && !action.show(props.data)) return null;
        const Renderer = ACTION_RENDERERS[action.type] as React.ComponentType<
          CustomCellRendererProps & Record<string, unknown>
        >;
        const handlerProp = handlerPropMap[action.type];
        const handler = action.idField
          ? () => action.handler(props.data[action.idField!])
          : action.handler;
        const extraProps = handlerProp ? { [handlerProp]: handler, cyTag: action.cyTag } : {};
        return <Renderer key={action.type + action.cyTag} {...props} {...extraProps} />;
      })}
    </div>
  );
};
